import { ChevronDown, ChevronUp, History, SearchX } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { normalizeCollection } from '../api/client'
import type { AuditLogEntry, User } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { Badge, Button, CachedNotice, EmptyState, ErrorState, LoadingState, Pagination } from '../components/ui'
import { useOfflineQuery } from '../hooks/useOfflineQuery'
import { formatDate, formatNumber } from '../utils/format'

const ENTITY_TYPES: Array<{ value: string; label: string }> = [
  { value: 'WorkOrder', label: 'Задание' }, { value: 'Defect', label: 'Дефект' }, { value: 'Equipment', label: 'Оборудование' }, { value: 'MaintenanceProgram', label: 'Программа ТО' },
  { value: 'ChecklistTemplateItem', label: 'Пункт чек-листа' }, { value: 'PprDocument', label: 'ППР' }, { value: 'MaintenancePlan', label: 'План' }, { value: 'Report', label: 'Отчёт' }, { value: 'User', label: 'Сотрудник' },
  { value: 'MaintenanceObject', label: 'Справочники: объекты' }, { value: 'AssetSystem', label: 'Справочники: системы' }, { value: 'EquipmentType', label: 'Справочники: типы оборудования' }, { value: 'MaintenanceType', label: 'Справочники: виды ТО' },
]
const ENTITY_LABELS: Record<string, string> = {
  ...Object.fromEntries(ENTITY_TYPES.map((item) => [item.value, item.label])),
  MaintenanceObject: 'Объект', AssetSystem: 'Система', EquipmentType: 'Тип оборудования', MaintenanceType: 'Вид ТО', EquipmentComponent: 'Компонент', ChecklistResult: 'Ответ чек-листа',
  Photo: 'Фото', AuditRun: 'Проверка данных', ReportTemplate: 'Шаблон отчёта', PlanItem: 'Позиция плана', Measurement: 'Измерение',
}
const ENTITY_ROUTES: Record<string, string> = { WorkOrder: '/work-orders', Defect: '/defects', Equipment: '/equipment', MaintenancePlan: '/plans', PprDocument: '/imports' }

const ACTION_PREFIXES: Array<[string, string]> = [
  ['WORK_ORDER', 'Задание'], ['WORKORDER', 'Задание'], ['DEFECT', 'Дефект'], ['EQUIPMENT_COMPONENT', 'Компонент'], ['COMPONENT', 'Компонент'], ['EQUIPMENT_TYPE', 'Тип оборудования'], ['EQUIPMENT', 'Оборудование'],
  ['MAINTENANCE_PROGRAM', 'Программа ТО'], ['PROGRAM_VERSION', 'Версия программы'], ['PROGRAM', 'Программа ТО'], ['CHECKLIST_TEMPLATE_ITEM', 'Пункт чек-листа'], ['CHECKLIST_ITEM', 'Пункт чек-листа'],
  ['CHECKLIST_RESULT', 'Ответ чек-листа'], ['CHECKLIST', 'Чек-лист'], ['PPR_DOCUMENT', 'ППР'], ['PPR', 'ППР'], ['IMPORT', 'Импорт ППР'], ['MAINTENANCE_PLAN', 'План'], ['PLAN_ITEM', 'Позиция плана'], ['PLAN', 'План'],
  ['REPORT_TEMPLATE', 'Шаблон отчёта'], ['REPORT', 'Отчёт'], ['AUDIT_RUN', 'Проверка данных'], ['AUDIT', 'Проверка данных'], ['USER', 'Сотрудник'], ['PHOTO', 'Фото'], ['MEASUREMENT', 'Измерение'],
  ['MAINTENANCE_OBJECT', 'Объект'], ['OBJECT', 'Объект'], ['ASSET_SYSTEM', 'Система'], ['SYSTEM', 'Система'], ['MAINTENANCE_TYPE', 'Вид ТО'], ['AUTH', 'Вход'], ['SESSION', 'Сессия'],
].sort((a, b) => b[0].length - a[0].length) as Array<[string, string]>

const ACTION_VERBS: Record<string, string> = {
  CREATED: 'создано', UPDATED: 'изменено', DELETED: 'удалено', STARTED: 'начато', COMPLETED: 'завершено', ASSIGNED: 'назначен исполнитель', UNASSIGNED: 'снят исполнитель', TRANSFERRED: 'перенесено',
  CANCELLED: 'отменено', REOPENED: 'возвращено в план', REVIEWED: 'проверено', ACCEPTED: 'принято', REJECTED: 'возвращено на доработку', APPROVED: 'утверждено', GENERATED: 'сформировано', CONFIRMED: 'подтверждено',
  UPLOADED: 'загружено', RESOLVED: 'устранено', CLOSED: 'закрыто', STATUS_CHANGED: 'изменено состояние', COMMENTED: 'добавлен комментарий', PHOTO_ADDED: 'добавлено фото', PHOTO_DELETED: 'удалено фото',
  VERSION_CREATED: 'создана новая версия', RESULTS_SAVED: 'сохранены ответы', RESULT_SAVED: 'сохранён ответ', MEASUREMENT_ADDED: 'добавлено измерение', DEFECT_CREATED: 'создан дефект', LOGIN: 'вход в систему',
  LOGOUT: 'выход', PASSWORD_CHANGED: 'изменён пароль', ROLE_CHANGED: 'изменены роли', ROLES_CHANGED: 'изменены роли', ACTIVATED: 'активировано', DEACTIVATED: 'деактивировано', SUPERSEDED: 'заменено новой версией',
  RUN: 'выполнено', DOWNLOADED: 'скачано', EXPORTED: 'выгружено', IMPORTED: 'импортировано', LOCKED: 'заблокировано', RESTORED: 'восстановлено', ARCHIVED: 'архивировано', REGISTERED: 'зарегистрировано',
  INVITED: 'приглашён', SCHEDULED: 'запланировано', REVIEW_REQUESTED: 'отправлено на проверку', ITEM_ADDED: 'добавлен пункт', ITEM_UPDATED: 'изменён пункт', ITEM_DELETED: 'удалён пункт', WORK_ORDERS_CREATED: 'созданы задания',
}

function humanize(value: string): string {
  const text = value.toLowerCase().replace(/_/g, ' ').trim()
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : value
}

/** WORK_ORDER_COMPLETED → «Задание: завершено»; неизвестные коды приводятся к читаемому виду. */
export function actionLabel(action: string): string {
  const upper = action.toUpperCase()
  for (const [prefix, entity] of ACTION_PREFIXES) {
    if (upper === prefix) return entity
    if (upper.startsWith(`${prefix}_`)) {
      const rest = upper.slice(prefix.length + 1)
      return `${entity}: ${ACTION_VERBS[rest] || humanize(rest).toLowerCase()}`
    }
  }
  return ACTION_VERBS[upper] ? humanize(ACTION_VERBS[upper]) : humanize(upper)
}

export function entityLabel(entityType: string): string {
  return ENTITY_LABELS[entityType] || entityType
}

function stringify(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Да' : 'Нет'
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>
    if (typeof record.fullName === 'string') return record.fullName
    if (typeof record.name === 'string') return record.name
    return JSON.stringify(value)
  }
  return String(value)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

export interface DiffRow { key: string; before: string; after: string }

/** Собирает компактную разницу «было → стало» из data (before/after, old/new, changes) либо плоский список полей. */
export function diffRows(data: Record<string, unknown> | null | undefined): { rows: DiffRow[]; flat: Array<[string, string]> } {
  if (!isRecord(data)) return { rows: [], flat: [] }
  const pairs: Array<[unknown, unknown]> = [[data.before, data.after], [data.old, data.new], [data.previous, data.current]]
  for (const [before, after] of pairs) {
    if (!isRecord(before) && !isRecord(after)) continue
    const left = isRecord(before) ? before : {}
    const right = isRecord(after) ? after : {}
    const keys = [...new Set([...Object.keys(left), ...Object.keys(right)])]
    const rows = keys.filter((key) => JSON.stringify(left[key] ?? null) !== JSON.stringify(right[key] ?? null)).map((key) => ({ key, before: stringify(left[key]), after: stringify(right[key]) }))
    const rest = Object.entries(data).filter(([key]) => !['before', 'after', 'old', 'new', 'previous', 'current'].includes(key)).map(([key, value]) => [key, stringify(value)] as [string, string])
    return { rows, flat: rest }
  }
  if (isRecord(data.changes)) {
    const rows = Object.entries(data.changes).map(([key, value]) => {
      if (Array.isArray(value) && value.length === 2) return { key, before: stringify(value[0]), after: stringify(value[1]) }
      if (isRecord(value) && ('from' in value || 'to' in value)) return { key, before: stringify(value.from), after: stringify(value.to) }
      return { key, before: '—', after: stringify(value) }
    })
    const rest = Object.entries(data).filter(([key]) => key !== 'changes').map(([key, value]) => [key, stringify(value)] as [string, string])
    return { rows, flat: rest }
  }
  return { rows: [], flat: Object.entries(data).map(([key, value]) => [key, stringify(value)] as [string, string]) }
}

function actionOptions(payload: unknown): Array<{ value: string; label: string }> {
  if (!Array.isArray(payload)) return []
  return payload.map((item) => {
    if (typeof item === 'string') return { value: item, label: actionLabel(item) }
    if (isRecord(item)) {
      const value = String(item.code ?? item.action ?? item.value ?? item.id ?? '')
      return { value, label: typeof item.label === 'string' ? item.label : actionLabel(value) }
    }
    return { value: '', label: '' }
  }).filter((item) => item.value)
}

export function AuditLogPage() {
  const { hasAnyRole } = useAuth()
  const planner = hasAnyRole('ROLE_PLANNER')
  const [entityType, setEntityType] = useState('')
  const [action, setAction] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [actorId, setActorId] = useState('')
  const [page, setPage] = useState(1)
  const [expanded, setExpanded] = useState<string | null>(null)
  const limit = 50
  const query = useMemo(() => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) })
    if (entityType) params.set('entityType', entityType)
    if (action) params.set('action', action)
    if (from) params.set('from', from)
    if (to) params.set('to', to)
    if (actorId) params.set('actorId', actorId)
    return params.toString()
  }, [action, actorId, entityType, from, page, to])
  const resource = useOfflineQuery<unknown>(`audit-log:${query}`, `/audit-log?${query}`, { raw: true, maxAgeMs: 60_000 })
  const actions = useOfflineQuery<unknown>('audit-log:actions', '/audit-log/actions', { maxAgeMs: 60 * 60_000 })
  const actors = useOfflineQuery<User[]>('work-orders:assignees', '/work-orders/assignees', { maxAgeMs: 10 * 60_000, enabled: planner })
  const collection = useMemo(() => (resource.data ? normalizeCollection<AuditLogEntry>(resource.data, page, limit) : null), [resource.data, page])
  const actionList = useMemo(() => actionOptions(actions.data), [actions.data])
  const filtersActive = Boolean(entityType || action || from || to || actorId)

  function choose(setter: (value: string) => void) {
    return (value: string) => { setter(value); setPage(1); setExpanded(null) }
  }

  function reset() { setEntityType(''); setAction(''); setFrom(''); setTo(''); setActorId(''); setPage(1); setExpanded(null) }

  return <div className="page-stack">
    <section className="page-heading"><div><p className="eyebrow">Контроль и прозрачность</p><h2>История изменений</h2><p>Кто, когда и что изменил в заданиях, дефектах, оборудовании, программах и отчётах.</p></div></section>
    {resource.source === 'cache' && resource.fetchedAt && <CachedNotice fetchedAt={resource.fetchedAt} stale={resource.stale} />}
    <section className="surface surface--flush">
      <div className="table-toolbar table-toolbar--wrap">
        <select name="auditEntityType" aria-label="Тип объекта" value={entityType} onChange={(event) => choose(setEntityType)(event.target.value)}><option value="">Все объекты</option>{ENTITY_TYPES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
        <select name="auditAction" aria-label="Действие" value={action} onChange={(event) => choose(setAction)(event.target.value)}><option value="">Все действия</option>{actionList.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
        <input name="auditFrom" className="toolbar-input" type="date" aria-label="С даты" value={from} max={to || undefined} onChange={(event) => choose(setFrom)(event.target.value)} />
        <input name="auditTo" className="toolbar-input" type="date" aria-label="По дату" value={to} min={from || undefined} onChange={(event) => choose(setTo)(event.target.value)} />
        {planner && <select name="auditActorId" aria-label="Сотрудник" value={actorId} onChange={(event) => choose(setActorId)(event.target.value)}><option value="">Все сотрудники</option>{(actors.data || []).map((person) => <option key={person.id} value={person.id}>{person.fullName}</option>)}</select>}
        {filtersActive && <Button variant="quiet" onClick={reset}>Сбросить</Button>}
        {collection && <span className="toolbar-count">Всего: {formatNumber(collection.meta.total)}</span>}
      </div>
      {resource.loading && <LoadingState label="Загружаем историю изменений" />}
      {resource.error && <ErrorState error={resource.error} onRetry={() => void resource.refresh()} />}
      {collection && collection.items.length === 0 && <EmptyState icon={filtersActive ? <SearchX /> : <History />} title="Записей нет" description={filtersActive ? 'Измените фильтры или период.' : 'Изменения появятся здесь по мере работы с системой.'} />}
      {collection && collection.items.length > 0 && <>
        <div className="responsive-table mg-audit-table"><table><thead><tr><th>Время</th><th>Кто</th><th>Действие</th><th>Объект</th><th><span className="sr-only">Подробнее</span></th></tr></thead><tbody>{collection.items.map((entry) => {
          const open = expanded === entry.id
          const { rows, flat } = diffRows(entry.data)
          const route = ENTITY_ROUTES[entry.entityType]
          return [
            <tr key={entry.id} className={open ? 'mg-log-row mg-log-row--open' : 'mg-log-row'}>
              <td data-label="Время"><strong>{formatDate(entry.createdAt, true)}</strong></td>
              <td data-label="Кто">{entry.actor?.fullName || 'Система'}</td>
              <td data-label="Действие"><strong>{actionLabel(entry.action)}</strong><small className="table-subline">{entry.action}</small></td>
              <td data-label="Объект"><Badge tone="neutral">{entityLabel(entry.entityType)}</Badge> <span className="mg-mono">{route ? <Link className="text-link" to={`${route}/${entry.entityId}`}>{entry.entityId.slice(0, 8)}</Link> : entry.entityId.slice(0, 8)}</span></td>
              <td data-label="Подробнее"><Button variant="quiet" aria-expanded={open} aria-controls={`audit-details-${entry.id}`} onClick={() => setExpanded(open ? null : entry.id)}>{open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}Подробнее</Button></td>
            </tr>,
            open ? <tr key={`${entry.id}-details`} className="mg-log-details" id={`audit-details-${entry.id}`}><td colSpan={5} data-label="Изменения">
              {rows.length === 0 && flat.length === 0 && <p className="quiet-placeholder">Дополнительных данных нет.</p>}
              {rows.length > 0 && <dl className="mg-diff">{rows.map((row) => <div key={row.key}><dt>{row.key}</dt><dd><span className="mg-diff__before">{row.before}</span><span aria-hidden="true">→</span><span className="mg-diff__after">{row.after}</span></dd></div>)}</dl>}
              {flat.length > 0 && <dl className="mg-diff mg-diff--flat">{flat.map(([key, value]) => <div key={key}><dt>{key}</dt><dd>{value}</dd></div>)}</dl>}
              {entry.requestId && <small className="mg-muted">Запрос {entry.requestId}</small>}
            </td></tr> : null,
          ]
        })}</tbody></table></div>
        <Pagination page={collection.meta.page} limit={collection.meta.limit} total={collection.meta.total} onChange={(next) => { setPage(next); setExpanded(null) }} />
      </>}
    </section>
  </div>
}
